/**
=========================================================
* Elite 247 Admin Panel - Split Screen Login
=========================================================
*/

import { useState, useEffect } from "react";
// Utility to fetch settings (minimal, for logo)
function usePanelSettings() {
  const [settings, setSettings] = useState({});
  useEffect(() => {
    // Public name -> value map (no sign-in needed); a relative /api/settings call never
    // reached the API server and needs a permission anyway.
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:4901/api";
    fetch(`${apiUrl}/settings/map`)
      .then((res) => res.json())
      .then((data) => setSettings(data || {}))
      .catch(() => {});
  }, []);
  return settings;
}
import { useNavigate, useSearchParams } from "react-router-dom";

// @mui material components
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Alert from "@mui/material/Alert";
import LinearProgress from "@mui/material/LinearProgress";

// @mui icons
import SecurityIcon from "@mui/icons-material/Security";
import CloudIcon from "@mui/icons-material/Cloud";
import AnalyticsIcon from "@mui/icons-material/Analytics";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MicrosoftIcon from "components/MicrosoftIcon";
import GoogleIcon from "components/GoogleIcon";

// Material Dashboard 2 React context
import { useMaterialUIController, setLayout } from "context";

// Context
import { useAuth } from "context/AuthContext";
import { signInRedirect } from "layouts/authentication/signedInRedirect";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4901/api";

function SignIn() {
  const settings = usePanelSettings();
  const [controller, dispatch] = useMaterialUIController();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState("");

  // Set layout to "page" to hide sidebar and settings icon
  useEffect(() => {
    setLayout(dispatch, "page");

    // Cleanup: Set layout back to "dashboard" when component unmounts
    return () => {
      setLayout(dispatch, "dashboard");
    };
  }, [dispatch]);

  // Already signed in (a session token is stored): go to the dashboard instead of showing
  // the sign-in buttons. Not while finishing an SSO round-trip or showing an error.
  useEffect(() => {
    const target = signInRedirect(searchParams);
    if (target) navigate(target, { replace: true });
  }, [searchParams, navigate]);

  // Handle OAuth callback
  useEffect(() => {
    const token = searchParams.get("token");
    const errorParam = searchParams.get("error");

    if (token) {
      // Store token and load user before redirecting
      const loadUserAndRedirect = async () => {
        try {
          localStorage.setItem("adminToken", token);
          // Let AuthContext handle loading the user
          navigate("/dashboard", { replace: true });
        } catch (err) {
          console.error("Failed to load user:", err);
          setError("Failed to complete authentication");
          setMicrosoftLoading(false);
        }
      };
      loadUserAndRedirect();
    } else if (errorParam) {
      setError(decodeURIComponent(errorParam));
      setMicrosoftLoading(false);
      setGoogleLoading(false);
    }
  }, [searchParams, navigate]);

  const handleMicrosoftLogin = () => {
    setMicrosoftLoading(true);
    setError("");

    // Redirect to server OAuth endpoint
    window.location.href = `${API_URL}/auth/microsoft`;
  };

  const handleGoogleLogin = () => {
    setGoogleLoading(true);
    setError("");

    // Redirect to server OAuth endpoint
    window.location.href = `${API_URL}/auth/google`;
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        width: "100%",
        overflow: "hidden",
      }}
    >
      {/* Left Side - Branding & Information */}
      <Box
        sx={{
          width: "50%",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: "60px",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)
            `,
            pointerEvents: "none",
          },
          "@media (max-width: 960px)": {
            display: "none",
          },
        }}
      >
        {/* Logo/Brand Section */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            textAlign: "center",
            mb: 6,
          }}
        >
          <Box
            sx={{
              width: "150px",
              height: "150px",
              margin: "0 auto 2rem",
              background: "rgba(255, 255, 255, 0.95)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backdropFilter: "blur(10px)",
              border: "3px solid rgba(255, 255, 255, 0.3)",
              boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
              animation: "float 3s ease-in-out infinite",
              padding: "15px",
            }}
          >
            <img
              src={settings.site_logo || "/apple-icon.png"}
              alt="Elite 247 Logo"
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </Box>
          <MDTypography
            variant="h1"
            fontWeight="bold"
            color="white"
            sx={{
              fontSize: "3.5rem",
              mb: 1,
              textShadow: "0 4px 20px rgba(0,0,0,0.3)",
            }}
          >
            Elite 247
          </MDTypography>
          <MDTypography
            variant="h4"
            color="white"
            sx={{
              opacity: 0.95,
              fontWeight: 300,
              letterSpacing: "2px",
            }}
          >
            Consult Admin
          </MDTypography>
        </Box>

        {/* Welcome Message & Security Info */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            width: "100%",
            maxWidth: "480px",
            textAlign: "center",
          }}
        >
          <MDTypography variant="h5" color="white" fontWeight="bold" sx={{ mb: 2, opacity: 0.95 }}>
            Secure Access Portal
          </MDTypography>
          <MDTypography
            variant="body1"
            color="white"
            sx={{
              mb: 5,
              opacity: 0.9,
              lineHeight: 1.8,
              fontSize: "1.1rem",
            }}
          >
            Manage your IT consulting services, bootcamp programs, blog content, and client
            communications all in one place.
          </MDTypography>

          {/* Security & Info Cards */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
              mb: 4,
            }}
          >
            {/* Authorized Access */}
            <Box
              sx={{
                background: "rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(10px)",
                borderRadius: "16px",
                padding: "20px 24px",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                gap: 2,
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "rgba(255, 255, 255, 0.2)",
                  transform: "translateX(5px)",
                },
              }}
            >
              <SecurityIcon sx={{ fontSize: 32, color: "white" }} />
              <Box sx={{ textAlign: "left", flex: 1 }}>
                <MDTypography variant="h6" color="white" fontWeight="bold" sx={{ mb: 0.5 }}>
                  Authorized Personnel Only
                </MDTypography>
                <MDTypography variant="body2" color="white" sx={{ opacity: 0.9 }}>
                  Enterprise-grade security with 256-bit SSL encryption
                </MDTypography>
              </Box>
            </Box>

            {/* 24/7 Access */}
            <Box
              sx={{
                background: "rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(10px)",
                borderRadius: "16px",
                padding: "20px 24px",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                gap: 2,
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "rgba(255, 255, 255, 0.2)",
                  transform: "translateX(5px)",
                },
              }}
            >
              <CloudIcon sx={{ fontSize: 32, color: "white" }} />
              <Box sx={{ textAlign: "left", flex: 1 }}>
                <MDTypography variant="h6" color="white" fontWeight="bold" sx={{ mb: 0.5 }}>
                  24/7 Cloud Access
                </MDTypography>
                <MDTypography variant="body2" color="white" sx={{ opacity: 0.9 }}>
                  Manage your platform from anywhere, anytime
                </MDTypography>
              </Box>
            </Box>

            {/* Real-time Updates */}
            <Box
              sx={{
                background: "rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(10px)",
                borderRadius: "16px",
                padding: "20px 24px",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                gap: 2,
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "rgba(255, 255, 255, 0.2)",
                  transform: "translateX(5px)",
                },
              }}
            >
              <AnalyticsIcon sx={{ fontSize: 32, color: "white" }} />
              <Box sx={{ textAlign: "left", flex: 1 }}>
                <MDTypography variant="h6" color="white" fontWeight="bold" sx={{ mb: 0.5 }}>
                  Real-Time Analytics
                </MDTypography>
                <MDTypography variant="body2" color="white" sx={{ opacity: 0.9 }}>
                  Monitor performance and track key metrics instantly
                </MDTypography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Bottom Text */}
        <Box
          sx={{
            position: "absolute",
            bottom: 40,
            textAlign: "center",
            zIndex: 1,
          }}
        >
          <MDTypography variant="body2" color="white" sx={{ opacity: 0.8 }}>
            © 2026 Elite 247 Consult. All rights reserved.
          </MDTypography>
        </Box>
      </Box>

      {/* Right Side - Login Form */}
      <Box
        sx={{
          width: "50%",
          backgroundColor: "#ffffff",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: { xs: "24px", md: "60px" },
          position: "relative",
          "@media (max-width: 960px)": {
            width: "100%",
          },
        }}
      >
        <Container maxWidth="sm">
          {/* Login Header */}
          <Box sx={{ mb: 5, textAlign: "center" }}>
            <MDTypography
              variant="h2"
              fontWeight="bold"
              sx={{
                mb: 1,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Welcome Back
            </MDTypography>
            <MDTypography variant="body1" color="text" sx={{ opacity: 0.7 }}>
              Sign in to access your admin dashboard
            </MDTypography>
          </Box>

          {/* Loading Bar */}
          {(microsoftLoading || googleLoading) && (
            <Box sx={{ mb: 3 }}>
              <LinearProgress
                sx={{
                  borderRadius: "4px",
                  height: "4px",
                  background: "rgba(102, 126, 234, 0.2)",
                  "& .MuiLinearProgress-bar": {
                    background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
                  },
                }}
              />
            </Box>
          )}

          {/* Error Alert */}
          {error && (
            <Box sx={{ mb: 3 }}>
              <Alert
                severity="error"
                onClose={() => setError("")}
                sx={{
                  borderRadius: "12px",
                  backgroundColor: "rgba(211, 47, 47, 0.1)",
                  border: "1px solid rgba(211, 47, 47, 0.3)",
                }}
              >
                {error}
              </Alert>
            </Box>
          )}

          {/* Login Form */}
          <Box>
            {/* Microsoft Sign In Button */}
            <MDButton
              sx={{
                background: "#ffffff",
                color: "#5e5e5e !important",
                fontWeight: "bold",
                fontSize: "1rem",
                padding: "14px",
                borderRadius: "12px",
                textTransform: "none",
                border: "2px solid #8c8c8c",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "#f3f3f3",
                  border: "2px solid #5e5e5e",
                  boxShadow: "0 6px 16px rgba(0, 0, 0, 0.15)",
                  transform: "translateY(-1px)",
                  color: "#5e5e5e !important",
                },
                "&:active": {
                  transform: "translateY(0)",
                  color: "#5e5e5e !important",
                },
                "&.Mui-disabled": {
                  background: "#f3f3f3",
                  color: "#5e5e5e !important",
                  opacity: 0.6,
                  border: "2px solid #cccccc",
                },
              }}
              fullWidth
              onClick={handleMicrosoftLogin}
              disabled={microsoftLoading || googleLoading}
              startIcon={microsoftLoading ? null : <MicrosoftIcon />}
            >
              {microsoftLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <span>Connecting to Microsoft</span>
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      width: "20px",
                      height: "20px",
                      border: "3px solid rgba(0,0,0,0.1)",
                      borderTop: "3px solid #5e5e5e",
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                    }}
                  />
                </Box>
              ) : (
                "Sign in with Microsoft"
              )}
            </MDButton>

            {/* Google Sign In Button */}
            <MDButton
              sx={{
                mt: 2,
                background: "#ffffff",
                color: "#5e5e5e !important",
                fontWeight: "bold",
                fontSize: "1rem",
                padding: "14px",
                borderRadius: "12px",
                textTransform: "none",
                border: "2px solid #8c8c8c",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "#f3f3f3",
                  border: "2px solid #5e5e5e",
                  boxShadow: "0 6px 16px rgba(0, 0, 0, 0.15)",
                  transform: "translateY(-1px)",
                  color: "#5e5e5e !important",
                },
                "&:active": {
                  transform: "translateY(0)",
                  color: "#5e5e5e !important",
                },
                "&.Mui-disabled": {
                  background: "#f3f3f3",
                  color: "#5e5e5e !important",
                  opacity: 0.6,
                  border: "2px solid #cccccc",
                },
              }}
              fullWidth
              onClick={handleGoogleLogin}
              disabled={googleLoading || microsoftLoading}
              startIcon={googleLoading ? null : <GoogleIcon />}
            >
              {googleLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <span>Connecting to Google</span>
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      width: "20px",
                      height: "20px",
                      border: "3px solid rgba(0,0,0,0.1)",
                      borderTop: "3px solid #5e5e5e",
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                    }}
                  />
                </Box>
              ) : (
                "Sign in with Google"
              )}
            </MDButton>

            {/* Security Badge */}
            <Box sx={{ mt: 4, textAlign: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                <SecurityIcon sx={{ fontSize: 16, color: "#667eea" }} />
                <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
                  Secured with 256-bit SSL encryption
                </MDTypography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Floating Animation Keyframes */}
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
      `}</style>
    </Box>
  );
}

export default SignIn;
